/* ============================================================================
   ag-ux-home.js — Armour Guards · HOME & DISCOVERY lane (2026-08-21)
   ----------------------------------------------------------------------------
   One finder engine for the whole discovery surface:
     §1 dataset + normalization/alias/typo matching (local, instant)
     §2 dropdown rendering + full keyboard/combobox a11y
     §3 fit panel: live products from the model hub, image-led fit rows
     §4 returning-customer chip (localStorage, no server state)
     §5 boot: any .agf2-widget on the page (home hero + /pages/cars)

   Depends on window.AG_HOME_DATA (snippets/ag-ux-home-finder-data.liquid).
   Network use: ONE fetch per selection — <hub>/products.json?limit=8 (~5 KB,
   measured 590 ms cold on the live store). Everything else is local.
   No jQuery, no theme internals, resilient to absence of every optional node.
   ========================================================================== */
(function () {
  'use strict';
  if (window.__agUxHome) return;
  window.__agUxHome = true;

  var DATA = (window.AG_HOME_DATA || []).slice();
  var LOGOS = window.AG_HOME_LOGOS || {};
  var CDN = 'https://cdn.shopify.com/s/files/1/0738/9613/2928/files/';
  var STORE = 'agCar'; // localStorage key → {b, m}

  /* dataset can arrive AFTER us (lazy-boot path in ag-ux-home-search-upgrade):
     rebuild the index the first time it is actually needed. */
  var INDEX = null;
  function ensureIndex() {
    if (INDEX && INDEX.length) return INDEX;
    DATA = (window.AG_HOME_DATA || []).slice();
    LOGOS = window.AG_HOME_LOGOS || {};
    INDEX = DATA.map(function (d) {
      return {
        raw: d, brand: d.b, model: d.m,
        nNorm: norm(d.m), nSq: squish(d.m),
        bNorm: norm(d.b), bSq: squish(d.b),
        hay: norm(d.b + ' ' + d.m), haySq: squish(d.b + d.m)
      };
    });
    return INDEX;
  }

  /* ===== §1 matching ====================================================== */
  /* normalize: lowercase, drop dots/dashes in "XUV-700", collapse spaces.
     keep digits+letters so "i20", "xuv7xo", "3xo" survive. */
  function norm(s) {
    return String(s == null ? '' : s)
      .toLowerCase()
      .replace(/[.\u2032\u2019'"]/g, '')
      .replace(/[-_/\\]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
  function squish(s) { return norm(s).replace(/ /g, ''); }

  /* typed alias → canonical model name. Built from real query shapes
     (measured live suggest misses): xuv7xo→"XUV 7XO", swift dzire→Dzire… */
  var ALIAS = {
    'scorpio': 'Scorpio N', 'scorpion': 'Scorpio N',
    'xuv700': 'XUV 700', 'xuv7oo': 'XUV 700',
    'xuv7xo': 'XUV 7XO', 'xuv 7 xo': 'XUV 7XO',
    'xuv3xo': 'XUV 3XO', 'xuv300': 'XUV300', 'xuv400': 'XUV400',
    'dzire': 'Dzire', 'dizire': 'Dzire', 'dezure': 'Dzire',
    'swift dzire': 'Dzire', 'swiftdzire': 'Dzire',
    'wagonr': 'WagonR', 'wagon r': 'WagonR', 'wagon': 'WagonR',
    'innova': 'Innova', 'hycross': 'Innova Hycross', 'crysta': 'Innova',
    'grandvitara': 'Grand Vitara', 'grand vitara': 'Grand Vitara',
    'hyryder': 'Urban Cruiser Hyryder',
    'ecosport': 'Ecosport', 'eco sport': 'Ecosport',
    'creta2023': 'Creta', 'creta 2023': 'Creta', 'creta2024': 'Creta',
    'seltos2023': 'Seltos', 'seltos 2023': 'Seltos', 'seltos2019': 'Seltos',
    'nexon2023': 'Nexon', 'nexonev': 'Nexon EV',
    'zsev': 'ZS EV', 'zs ev': 'ZS EV', 'hector': 'Hector',
    'city': 'City', 'hondacity': 'City',
    'tharrox': 'Thar Roxx', 'thar roxx': 'Thar Roxx',
    'carensclavis': 'Carens Clavis',
    'urbancruiser': 'Urban Cruiser Hyryder',
    'series3': '3 Series', '3series': '3 Series', 'series5': '5 Series',
    'aclass': 'E-Class', 'eclass': 'E-Class', 'gls': 'GLS', 'glc': 'GLC'
  };

  /* index is built lazily via ensureIndex() — see note above */

  function yearTokens(q) {
    var m = norm(q).match(/\b(19|20)\d{2}\b/g);
    return m || [];
  }
  /* strip years from the query before matching models */
  function stripYears(q) {
    return norm(q).replace(/\b(19|20)\d{2}\b/g, ' ').replace(/\s+/g, ' ').trim();
  }

  /* levenshtein ≤1 for typo tolerance ("cret", "selto") — small N, cheap */
  function withinOne(a, b) {
    if (a === b) return true;
    var la = a.length, lb = b.length;
    if (Math.abs(la - lb) > 1) return false;
    var i = 0, j = 0, edits = 0;
    while (i < la && j < lb) {
      if (a[i] === b[j]) { i++; j++; continue; }
      if (++edits > 1) return false;
      if (la > lb) i++; else if (lb > la) j++; else { i++; j++; }
    }
    if (i < la || j < lb) edits++;
    return edits <= 1;
  }

  function searchModels(query) {
    var all = ensureIndex();
    if (!all.length) return [];
    var q = norm(query);
    if (!q) return [];
    var qSq = squish(q);
    var qBare = squish(stripYears(query));
    /* alias pass runs on BOTH the raw and year-stripped forms, and also on
       the first two words ("swift dzire 2018" → "swift dzire"). */
    var q2 = norm(stripYears(query));
    var aliasHit = ALIAS[q] || ALIAS[qBare] || ALIAS[q2] ||
                   (q2.indexOf(' ') > -1 && (ALIAS[q2.split(' ').slice(0, 2).join(' ')] || null));
    if (aliasHit) {
      var exact = all.filter(function (e) { return e.nNorm === norm(aliasHit); });
      if (exact.length) return exact;
    }
    var scored = [];
    for (var k = 0; k < all.length; k++) {
      var e = all[k], s = -1;
      if (e.nNorm === qBare || e.hay === qBare) s = 100;           // exact
      else if (e.nSq.indexOf(qSq) === 0) s = 90;                    // prefix
      else if (e.nSq.indexOf(qSq) > -1 || e.bSq.indexOf(qSq) > -1) s = 80;
      else if (qBare.length >= 3 &&
               (withinOne(e.nSq, qBare) || e.haySq.indexOf(qBare) > -1)) s = 70;
      else if (qBare.length >= 4) {
        // all tokens present somewhere ("kia selt")
        var toks = qBare.split(' '), ok = true;
        for (var t = 0; t < toks.length; t++) {
          if (toks[t].length < 2) continue;
          if (e.haySq.indexOf(toks[t]) === -1 && !withinOne(e.nSq, toks[t])) { ok = false; break; }
        }
        if (ok) s = 60;
      }
      if (s > 0) scored.push({ e: e, s: s });
    }
    scored.sort(function (a, b) {
      return b.s - a.s || a.e.model.length - b.e.model.length ||
             (a.e.brand < b.e.brand ? -1 : 1);
    });
    return scored.slice(0, 7).map(function (x) { return x.e; });
  }

  /* ===== §2 dropdown ====================================================== */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function logoHTML(brand) {
    var slug = LOGOS[brand];
    if (!slug) return '<span class="agf2-res__logo">' + esc(brand[0] || '?') + '</span>';
    return '<span class="agf2-res__logo"><img src="' + CDN + esc(slug) +
      '.png?width=80" alt="" loading="lazy" onerror="this.parentNode.textContent=\'' +
      esc((brand[0] || '?').replace(/'/g, '')) + '\'"></span>';
  }

  function widget(root) {
    if (root.dataset.agf2Ready) return;
    root.dataset.agf2Ready = '1';
    var input = root.querySelector('.agf2-input');
    var formEl = root.querySelector('.agf2-form');
    var box = root.querySelector('.agf2-results');
    var panel = root.querySelector('.agf2-panel');
    if (!input || !box) return;

    /* On the phone-sized hero the previous three-example placeholder needed
       286px inside a 209px content box. Keep one concrete example fully visible;
       /pages/cars has more width and retains its more detailed placeholder. */
    if (root.closest('.ag-ux-home-hero')) {
      input.setAttribute('placeholder', 'Your car — e.g. Creta');
    }

    var activeIdx = -1, debounce = 0;

    function rows() { return [].slice.call(box.querySelectorAll('.agf2-res')); }
    function open() {
      box.classList.add('open');
      input.setAttribute('aria-expanded', 'true');
    }
    function close() {
      /* fit loads are owned centrally by showFitPanel (newest-selection-wins);
         closing the dropdown must NOT kill a load whose panel is still open */
      box.classList.remove('open');
      box.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      activeIdx = -1;
    }
    function setActive(i) {
      var els = rows();
      if (!els.length) return;
      if (i < 0) i = els.length - 1;
      if (i >= els.length) i = 0;
      els.forEach(function (el, idx) {
        el.classList.toggle('is-active', idx === i);
        if (idx === i) el.setAttribute('aria-selected', 'true');
        else el.removeAttribute('aria-selected');
      });
      activeIdx = i;
      input.setAttribute('aria-activedescendant', els[i].id);
      els[i].scrollIntoView({ block: 'nearest' });
    }

    function paint(list, q) {
      if (!list.length) {
        box.innerHTML =
          '<div class="agf2-empty">No match for &ldquo;' + esc(q) + '&rdquo;. ' +
          '<a href="/pages/cars">Browse all brands &rarr;</a> or ' +
          '<a href="https://wa.me/917800111678?text=Hi%20Armour%20Guards%2C%20I%20need%20help%20finding%20a%20screen%20guard%20for%20my%20car" target="_blank" rel="noopener">WhatsApp us your car</a>.</div>';
        open();
        return;
      }
      box.innerHTML = list.map(function (e, i) {
        var sub = e.raw.n != null
          ? esc(e.brand) + ' · Screen Guards · ' + e.raw.n + ' fit' + (e.raw.n === 1 ? '' : 's')
          : esc(e.brand) + ' · Screen Guards';
        return '<button type="button" class="agf2-res" role="option" id="agf2-o' + i + '" data-b="' +
          esc(e.brand) + '" data-m="' + esc(e.model) + '">' +
          logoHTML(e.brand) +
          '<span class="agf2-res__meta"><span class="agf2-res__model">' + esc(e.model) + '</span>' +
          '<span class="agf2-res__sub">' + sub + '</span></span>' +
          '<span class="agf2-res__go" aria-hidden="true">&rsaquo;</span></button>';
      }).join('');
      [].forEach.call(box.querySelectorAll('.agf2-res'), function (el) {
        el.addEventListener('click', function () {
          selectModel(el.getAttribute('data-b'), el.getAttribute('data-m'));
        });
      });
      open();
      activeIdx = -1;
    }

    input.addEventListener('input', function () {
      clearTimeout(debounce);
      var v = input.value;
      if (norm(v).length < 1) { close(); hidePanel(); return; }
      debounce = setTimeout(function () { paint(searchModels(v), v.trim()); }, 60);
    });
    input.addEventListener('focus', function () {
      if (input.value.trim() && box.children.length) open();
    });
    input.addEventListener('keydown', function (ev) {
      var isOpen = box.classList.contains('open');
      if (ev.key === 'ArrowDown') { ev.preventDefault(); isOpen ? setActive(activeIdx + 1) : paint(searchModels(input.value), input.value.trim()); }
      else if (ev.key === 'ArrowUp' && isOpen) { ev.preventDefault(); setActive(activeIdx - 1); }
      else if (ev.key === 'Enter') {
        ev.preventDefault();
        if (isOpen && activeIdx >= 0) {
          var el = rows()[activeIdx];
          if (el) selectModel(el.getAttribute('data-b'), el.getAttribute('data-m'));
        } else {
          var hits = searchModels(input.value);
          if (hits.length === 1) selectModel(hits[0].brand, hits[0].model);
          else if (hits.length) paint(hits, input.value.trim());
          else submitFallback(input.value.trim());
        }
      }
      else if (ev.key === 'Escape' && isOpen) { ev.stopPropagation(); close(); }
    });
    document.addEventListener('click', function (ev) {
      if (!root.contains(ev.target)) close();
    });
    formEl && formEl.addEventListener('submit', function (ev) {
      ev.preventDefault();
      var hits = searchModels(input.value);
      if (hits.length === 1) selectModel(hits[0].brand, hits[0].model);
      else if (hits.length) paint(hits, input.value.trim());
      else submitFallback(input.value.trim());
    });

    function submitFallback(q) {
      if (!q) return;
      window.location.href = '/search?q=' + encodeURIComponent(q) + '&type=product';
    }

    /* popular chips + returning-customer chip */
    [].forEach.call(root.querySelectorAll('[data-agf2-pick]'), function (chip) {
      chip.addEventListener('click', function () {
        var m = chip.getAttribute('data-agf2-pick');
        var hit = ensureIndex().filter(function (e) { return e.nNorm === norm(m); })[0];
        if (hit) selectModel(hit.brand, hit.model);
      });
    });
    renderReturningChip(root);

    /* ===== §3 selection → fit panel ==================================== */
    function selectModel(brand, model) {
      input.value = brand + ' ' + model;
      close();
      remember(brand, model);
      showFitPanel(root, brand, model, panel);
    }
  }

  function hidePanelIn(root) {
    var p = root.querySelector('.agf2-panel');
    if (p) {
      if (p.classList.contains('open')) invalidateFitLoad(); /* panel going away → its load no longer matters */
      p.classList.remove('open'); p.innerHTML = '';
    }
  }
  function hidePanel() {
    [].forEach.call(document.querySelectorAll('.agf2-widget'), hidePanelIn);
  }

  var FIT_CACHE = {}; // hub URL → parsed rows (per pageview; tiny)

  function parseFit(title) {
    /* screen size: prefer explicit inches numbers (10.25″ / 10.25 inch / 8" ) */
    var size = '';
    var m = title.match(/(\d{1,2}(?:\.\d{1,2})?)\s*(?:″|"|”|inch\b|inches\b)/i);
    if (m) size = m[1] + '"';
    /* year range: "(2019–present)" "(2018–2020)" "(2024)" bare years in tags */
    var yr = title.match(/(\(?)(19|20)\d{2}\s*(?:[–—-]|to)?\s*(?:(19|20)\d{2}|present|running)?(\))?/i);
    var years = '';
    var ym = title.match(/((?:19|20)\d{2})\s*(?:[–—-]|to)\s*((?:19|20)\d{2}|present|running)/i);
    if (ym) years = ym[1] + '–' + (/present|running/i.test(ym[2]) ? 'now' : ym[2]);
    else {
      var y1 = title.match(/(?:^|[^0-9])((?:19|20)\d{2})(?:[^0-9]|$)/);
      if (y1) years = y1[1] + '+';
    }
    return { size: size, years: years };
  }
  function inr(v) { return '₹' + Math.round(parseFloat(v) || 0).toLocaleString('en-IN'); }

  var FIT_CTRL = null; /* AbortController of the in-flight fit load */
  var FIT_SEQ = 0;     /* bumped on every new selection / panel hide / retry */
  function invalidateFitLoad() {
    FIT_SEQ++;
    if (FIT_CTRL) { try { FIT_CTRL.abort(); } catch (e) {} FIT_CTRL = null; }
  }

  /* Panel head shared by every panel state. The count slot carries the brand
     while loading and the row count once loaded — same slot, honest states. */
  function panelHead(entry, countText) {
    return '<div class="agf2-panel__head"><h3 class="agf2-panel__title">' + esc(entry.model) +
      ' screen guards</h3><span class="agf2-panel__count">' +
      (countText != null ? esc(countText) : esc(entry.brand)) + '</span></div>';
  }
  function fitSkeletonHTML(entry) {
    return panelHead(entry) +
      '<div class="agf2-rows">' +
      '<div class="ux-skeleton agf2-skel"></div>' +
      '<div class="ux-skeleton agf2-skel"></div>' +
      '</div><div class="agf2-panel__cta"></div>';
  }

  /* The ONE loader behind both the initial selection and Try again: race the
     hub fetch against an 8 s killer and ALWAYS settle into paintFit — rows on
     success, the error state on anything else. Nothing may throw between the
     settle decision and the paint: last round paintFit read a variable it did
     not have (`hub`), so BOTH the catch path and the killer died mid-paint and
     the customer sat on the skeleton forever. Retry context therefore rides ON
     the button (data-b/data-m), not in a closure or the input's value. */
  function loadFits(panel, entry) {
    invalidateFitLoad(); /* newest selection wins */
    var seq = FIT_SEQ;
    var hub = entry.raw.u;
    var myCtrl = ('AbortController' in window) ? new AbortController() : null;
    FIT_CTRL = myCtrl;
    var done = false;
    var killer = setTimeout(function () {
      if (done || seq !== FIT_SEQ) return;
      if (myCtrl) { try { myCtrl.abort(); } catch (e) {} } /* stop paying for the hung request */
      finish(null);
    }, 8000);
    function finish(data) {
      if (done || seq !== FIT_SEQ) return; /* stale or already settled — never paint */
      done = true;
      clearTimeout(killer);
      FIT_CTRL = null;
      if (data) FIT_CACHE[hub] = data;
      paintFit(panel, data, entry);
    }
    fetch(hub + '/products.json?limit=8', {
      headers: { Accept: 'application/json' },
      signal: myCtrl ? myCtrl.signal : undefined
    })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(function (d) {
        finish((d.products || []).map(function (p) {
          return {
            title: p.title,
            url: '/products/' + p.handle,
            price: p.price_min || p.variants[0] && p.variants[0].price,
            image: p.images && p.images[0] ? p.images[0].src : ''
          };
        }));
      })
      .catch(function () { finish(null); });
  }

  function showFitPanel(root, brand, model, panel) {
    var entry = ensureIndex().filter(function (e) { return e.brand === brand && e.model === model; })[0];
    if (!panel || !entry) return;

    invalidateFitLoad(); /* newest selection wins */

    /* search-fallback models (no populated hub yet): be honest, route to search */
    if (entry.raw.p) {
      panel.innerHTML = panelHead(entry) +
        '<div class="agf2-rows"></div>' +
        '<div class="agf2-panel__cta">' +
        '<a class="ux-btn-primary" href="' + esc(entry.raw.p) + '">See ' + esc(model) + ' guards &rarr;</a>' +
        '<p class="agf2-panel__help">Not sure which one fits? <a href="https://wa.me/917800111678?text=Hi%20Armour%20Guards%2C%20I%20have%20a%20question%20before%20I%20order." target="_blank" rel="noopener">Ask us on WhatsApp</a> — we confirm before you buy.</p>' +
        '</div>';
      panel.classList.add('open');
      return;
    }

    if (FIT_CACHE[entry.raw.u]) { paintFit(panel, FIT_CACHE[entry.raw.u], entry); return; }

    panel.innerHTML = fitSkeletonHTML(entry);
    panel.classList.add('open');
    loadFits(panel, entry);
  }

  function retryFit(panel, entry) {
    /* back to loading, then through the same guarded loader as the first
       attempt — a retry is a fresh load, not a half-state */
    panel.innerHTML = fitSkeletonHTML(entry);
    panel.classList.add('open');
    loadFits(panel, entry);
  }

  document.addEventListener('click', function (ev) {
    var button = ev.target.closest ? ev.target.closest('[data-agf2-retry]') : null;
    if (!button) return;
    var panel = button.closest('.agf2-panel');
    if (!panel) return;
    /* preferred: the exact car stamped on the button when it was painted */
    var entry = null;
    var b = button.getAttribute('data-b'), m = button.getAttribute('data-m');
    if (b && m) {
      entry = ensureIndex().filter(function (e) { return e.brand === b && e.model === m; })[0];
    }
    /* fallback for markup without data-b/data-m: reconstruct "Brand Model" from the input */
    if (!entry) {
      var widgetRoot = panel.closest('.agf2-widget');
      var inputEl = widgetRoot ? widgetRoot.querySelector('.agf2-input') : null;
      var typed = inputEl ? inputEl.value : '';
      entry = ensureIndex().filter(function (candidate) {
        return typed.toLowerCase().indexOf((candidate.brand + ' ' + candidate.model).toLowerCase()) === 0;
      })[0];
    }
    if (!entry) return;
    ev.preventDefault();
    retryFit(panel, entry);
  });

  function paintFit(panel, rows, entry) {
    var head = panelHead(entry, rows && rows.length ? rows.length + ' cut to fit' : null);
    var body;
    if (!rows || !rows.length) {
      body = '<div class="agf2-error" role="status">' +
        '<p class="agf2-empty">We could not load the fits just now.</p>' +
        '<div class="agf2-error__actions">' +
        '<button type="button" class="ux-btn-secondary agf2-retry" data-agf2-retry data-b="' + esc(entry.brand) + '" data-m="' + esc(entry.model) + '">Try again</button>' +
        '<a class="agf2-error__link" href="' + esc(entry.raw.u) + '">See all ' + esc(entry.model) + ' guards</a>' +
        '<a class="agf2-error__link" href="https://wa.me/917800111678?text=Hi%20Armour%20Guards%2C%20the%20fits%20did%20not%20load%20for%20my%20car%3A%20' + encodeURIComponent(entry.brand + ' ' + entry.model) + '" target="_blank" rel="noopener">Ask on WhatsApp</a>' +
        '</div></div>';
    } else {
      body = '<div class="agf2-rows">' + rows.map(function (p) {
        var f = parseFit(p.title);
        var was = ''; /* compare-at not present in collection JSON; omit rather than invent */
        var media = '<span class="agf2-row__media' + (p.image ? '' : ' is-fallback') + '" aria-hidden="true">' +
          (p.image ? '<img src="' + esc(p.image) + '" alt="" width="64" height="64" loading="lazy" decoding="async" data-agf2-image>' : '') +
          '<svg class="agf2-row__fallback" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" focusable="false"><circle cx="7.5" cy="7.5" r="1.5"></circle><path d="M2.5 12C2.5 7.52 2.5 5.28 3.89 3.89C5.28 2.5 7.52 2.5 12 2.5C16.48 2.5 18.72 2.5 20.11 3.89C21.5 5.28 21.5 7.52 21.5 12C21.5 16.48 21.5 18.72 20.11 20.11C18.72 21.5 16.48 21.5 12 21.5C7.52 21.5 5.28 21.5 3.89 20.11C2.5 18.72 2.5 16.48 2.5 12Z"></path><path d="M5 21C9.37 15.78 14.27 8.88 21.5 13.54"></path></svg></span>';
        var details = (f.years || f.size) ? '<span class="agf2-row__details">' +
          (f.years ? '<span class="agf2-row__years">' + esc(f.years) + '</span>' : '') +
          (f.size ? '<span class="agf2-row__size"><svg class="agf2-row__size-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M17.5 10.5L19.5 12.5M14 14L16 16M10.5 17.5L12.5 19.5"></path><path d="M10.54 4.68C11.9 3.31 12.58 2.63 13.34 2.32C14.36 1.89 15.5 1.89 16.51 2.32C17.28 2.63 17.96 3.31 19.32 4.68C20.69 6.04 21.37 6.72 21.68 7.49C22.11 8.5 22.11 9.64 21.68 10.66C21.37 11.42 20.69 12.1 19.32 13.46L13.46 19.32C12.1 20.69 11.42 21.37 10.66 21.68C9.64 22.11 8.5 22.11 7.49 21.68C6.72 21.37 6.04 20.69 4.68 19.32C3.31 17.96 2.63 17.28 2.32 16.51C1.89 15.5 1.89 14.36 2.32 13.34C2.63 12.58 3.31 11.9 4.68 10.54L10.54 4.68Z"></path></svg><span>' + esc(f.size) + ' screen</span></span>' : '') +
          '</span>' : '';
        return '<a class="agf2-row" href="' + esc(p.url) + '">' +
          media +
          '<span class="agf2-row__meta"><span class="agf2-row__name">' + esc(p.title) + '</span>' +
          details +
          '</span>' +
          '<span class="agf2-row__price">' + esc(inr(p.price)) + was + '</span>' +
          '<span class="agf2-row__go" aria-hidden="true">&rsaquo;</span></a>';
      }).join('') + '</div>';
    }
    var cta =
      '<div class="agf2-panel__cta">' +
      '<a class="ux-btn-primary" href="' + esc(entry.raw.u) + '">See all ' + esc(entry.model) + ' guards &rarr;</a>' +
      '<p class="agf2-panel__help">Wrong screen size? <a href="https://wa.me/917800111678?text=' + encodeURIComponent('Hi Armour Guards, will this fit my car? ' + entry.brand + ' ' + entry.model) + '" target="_blank" rel="noopener">Send us your car &amp; variant on WhatsApp</a> — we confirm the fit first.</p>' +
      '</div>';
    panel.innerHTML = head + body + cta;
    panel.classList.add('open');
    [].forEach.call(panel.querySelectorAll('[data-agf2-image]'), function (image) {
      function showFallback() {
        image.parentNode.classList.add('is-fallback');
      }
      image.addEventListener('error', showFallback);
      if (image.complete && !image.naturalWidth) showFallback();
    });
  }

  /* ===== §4 returning customer ============================================ */
  function remember(b, m) {
    try { localStorage.setItem(STORE, JSON.stringify({ b: b, m: m })); } catch (e) {}
  }
  function recall() {
    try {
      var v = JSON.parse(localStorage.getItem(STORE) || 'null');
      return v && v.b && v.m ? v : null;
    } catch (e) { return null; }
  }
  function renderReturningChip(root) {
    var car = recall();
    if (!car) return;
    var hit = ensureIndex().filter(function (e) { return e.brand === car.b && e.model === car.m; })[0];
    if (!hit) return;
    if (root.querySelector('.agf2-carchip')) return;
    var chip = document.createElement('div');
    chip.className = 'agf2-carchip';
    chip.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 17a2 2 0 1 0 4 0a2 2 0 1 0 -4 0"/><path d="M15 17a2 2 0 1 0 4 0a2 2 0 1 0 -4 0"/><path d="M5 17h-2v-6l2 -5h9l4 5h1a2 2 0 0 1 2 2v4h-2"/></svg>' +
      'Your car: <a href="#" data-agf2-again>' + esc(car.b + ' ' + car.m) + '</a>' +
      '<button type="button" aria-label="Clear saved car" data-agf2-forget>&times;</button>';
    var formEl = root.querySelector('.agf2-form');
    formEl && formEl.parentNode.insertBefore(chip, formEl);
    chip.querySelector('[data-agf2-again]').addEventListener('click', function (ev) {
      ev.preventDefault();
      selectModelIn(root, car.b, car.m);
    });
    chip.querySelector('[data-agf2-forget]').addEventListener('click', function () {
      try { localStorage.removeItem(STORE); } catch (e) {}
      chip.remove();
    });
  }
  function selectModelIn(root, b, m) {
    var input = root.querySelector('.agf2-input');
    if (input) input.value = b + ' ' + m;
    remember(b, m);
    showFitPanel(root, b, m, root.querySelector('.agf2-panel'));
  }

  /* ===== §5 boot ========================================================== */
  function boot() {
    [].forEach.call(document.querySelectorAll('.agf2-widget'), widget);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* expose minimal API for the header-search upgrade snippet */
  window.agUxHomeSearch = {
    search: function (q) {
      var hits = searchModels(q);
      return hits.map(function (e) { return e.raw; });
    },
    esc: esc
  };
})();
